import { TenantRole } from '@bybeam/identity-client/types';
import { Nullable, PartnerConfig } from '@bybeam/platform-types';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import { StorageKeys } from '@platform-ui/@types/storage.js';
import env from '@platform-ui/utilities/env.js';
import { ReactNode, createContext, useContext, useMemo } from 'react';

interface TenantState {
  tenantId: Nullable<string>;
}

function resolveTenantId(tenantRole?: TenantRole, partnerConfig?: PartnerConfig): string {
  if (!tenantRole) return null;
  if (partnerConfig?.identity?.[tenantRole]?.tenantId)
    return partnerConfig?.identity?.[tenantRole]?.tenantId;
  const { REACT_APP_IDENTITY_APPLICANT_TENANT, REACT_APP_IDENTITY_ADVOCATE_TENANT } = env();
  if (tenantRole === 'advocate') return REACT_APP_IDENTITY_ADVOCATE_TENANT;
  return REACT_APP_IDENTITY_APPLICANT_TENANT;
}

// Only exported for use in tests/mocks; do NOT reference directly in prod code
export const TenantContext = createContext<TenantState>({} as TenantState);

export function TenantProvider({
  children,
  tenantRole,
}: {
  children: ReactNode;
  tenantRole?: TenantRole;
}): JSX.Element {
  const partner = usePartner();

  const value = useMemo(() => {
    let tenantId = localStorage.getItem(StorageKeys.GCIPTenantId);
    const authToken = localStorage.getItem(StorageKeys.GCIPToken);
    if (tenantId && !authToken) {
      tenantId = undefined;
      localStorage.clear();
    }
    if (!tenantId) tenantId = resolveTenantId(tenantRole, partner?.config);
    return { tenantId };
  }, [tenantRole, partner]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export default function useTenant(): TenantState {
  return useContext(TenantContext);
}
